import * as React from 'react';
import {
  FloatingNode,
  useFloatingParentNodeId,
  useFloatingTree,
  FloatingTree,
  OpenChangeReason,
} from '@floating-ui/react';

import {MenuProps} from './_types';
import {MenuContext, useMenu} from './useMenu';
import {MenuContentContext} from './useMenuContent';
import {AlignItemsSlim} from '../__SharedTypes';
import {useSpringOverlay} from '../SpringProvider/SpringOverlay';
import {useAnchorRefPosition} from '../../hooks';

export const MenuFloatingWrapper = React.forwardRef<
  HTMLElement,
  MenuProps & React.HTMLProps<HTMLElement>
>((props, ref) => {
  const isNested = useSpringOverlay();

  const TreeProvider = isNested ? React.Fragment : FloatingTree;

  return (
    <TreeProvider>
      <InternalMenu {...props} ref={ref} />
    </TreeProvider>
  );
});

const InternalMenu = React.forwardRef<
  HTMLElement,
  MenuProps & React.HTMLProps<HTMLElement>
>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      anchorRef,
      children,
      containerPadding,
      defaultIsOpen = false,
      gutter,
      height,
      isOpen: controlledIsOpen,
      maskProps,
      maxHeight,
      minHeight,
      onClose,
      onRequestClose,
      onRequestOpen,
      onOpen,
      placement,
      shouldAllowPointerEventsOutside = false,
      shouldCloseOnClickItem = true,
      shouldEscapeViewport = true,
      shouldHideOnClickOutside = true,
      shouldLoop = false,
      shouldPreventBodyScroll = false,
      shouldResizeMenuToMatchTrigger = false,
      shouldShowMask = false,
      shouldShowSelectedCheck = false,
      shouldTruncateText = false,
      maxWidth: maxWidthProp,
      minWidth: minWidthProp,
      verticalItemAlignment: verticalItemAlignmentProp,
      width: widthProp,
    },
    forwardedRef
  ) => {
    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
    const [isTriggerDisabled, setIsTriggerDisabled] =
      React.useState<boolean>(false);

    const [uncontrolledIsOpen, setUncontrolledIsOpen] =
      React.useState(defaultIsOpen);
    const isOpen = controlledIsOpen ?? uncontrolledIsOpen;

    /**
     * We want to control the Menu's open/close behavior when the MenuTrigger is disabled.
     * This prevents the Menu from opening when the MenuTrigger is disabled.
     */
    const onOpenChange = React.useCallback(
      (change: boolean, event?: Event, reason?: OpenChangeReason) => {
        if (change && isTriggerDisabled) return;
        if (controlledIsOpen === undefined) setUncontrolledIsOpen(change);

        // Callbacks
        if (change) {
          onRequestOpen?.();
          onOpen?.(event, reason);
        } else {
          onRequestClose?.();
          onClose?.(event, reason);
        }
      },
      [
        controlledIsOpen,
        isTriggerDisabled,
        onClose,
        onOpen,
        onRequestClose,
        onRequestOpen,
      ]
    );

    const elementsRef = React.useRef<Array<HTMLButtonElement | null>>([]);
    const parent = React.useContext(MenuContentContext);

    const tree = useFloatingTree();
    const parentId = useFloatingParentNodeId();
    const inheritedMenuContext = React.useContext(MenuContext);

    const isNested = inheritedMenuContext != null;

    let verticalItemAlignment: AlignItemsSlim = 'start';

    if (verticalItemAlignmentProp !== undefined) {
      verticalItemAlignment = verticalItemAlignmentProp;
    } else if (inheritedMenuContext?.verticalItemAlignment !== undefined) {
      verticalItemAlignment = inheritedMenuContext?.verticalItemAlignment;
    }

    let minWidth = minWidthProp;
    let maxWidth = maxWidthProp;

    if (widthProp) {
      minWidth = widthProp;
      maxWidth = widthProp;
    }

    const {
      context,
      floatingStyles,
      getFloatingProps,
      getItemProps,
      getReferenceProps,
      nodeId,
      refs,
    } = useMenu({
      activeIndex,
      containerPadding,
      elementsRef,
      gutter,
      isDisabled: isTriggerDisabled,
      isOpen,
      isNested,
      onOpenChange,
      placement,
      setActiveIndex,
      shouldEscapeViewport,
      shouldHideOnClickOutside,
      shouldLoop,
      shouldResizeMenuToMatchTrigger,
      maxHeight,
      maxWidth,
      minWidth,
    });

    // Event emitter allows you to communicate across tree components.
    // This effect closes all menus when an item gets clicked anywhere
    // in the tree.
    React.useEffect(() => {
      if (!tree) return;

      function handleTreeClick(event: {
        shouldCloseOnClickIndividualItem: MenuProps['shouldCloseOnClickItem'];
        originalEvent: React.MouseEvent;
      }) {
        if (event.shouldCloseOnClickIndividualItem ?? shouldCloseOnClickItem) {
          onOpenChange(false, event.originalEvent?.nativeEvent, 'focus-out');
        }
      }

      function onSubMenuOpen(event: {
        nodeId: string | undefined;
        parentId: string;
      }) {
        if (event.nodeId !== nodeId && event.parentId === parentId) {
          onOpenChange(false, undefined, 'focus-out');
        }
      }

      if (isOpen) {
        tree.events.on('click', handleTreeClick);
        tree.events.on('menuopen', onSubMenuOpen);
      }

      return () => {
        tree.events.off('click', handleTreeClick);
        tree.events.off('menuopen', onSubMenuOpen);
      };
    }, [isOpen, tree, nodeId, onOpenChange, parentId, shouldCloseOnClickItem]);

    React.useEffect(() => {
      if (isOpen && tree) {
        tree.events.emit('menuopen', {parentId, nodeId});
      }
    }, [tree, isOpen, nodeId, parentId]);

    useAnchorRefPosition({anchorRef, context});

    const menuContextValue = React.useMemo(
      () => ({
        activeIndex,
        ariaLabel,
        ariaLabelledBy,
        context,
        elementsRef,
        floatingStyles,
        getFloatingProps,
        getItemProps,
        getReferenceProps,
        height,
        isNested,
        maskProps,
        minHeight,
        nodeId,
        parent,
        parentId,
        refs,
        setActiveIndex,
        setIsTriggerDisabled,
        shouldAllowPointerEvents: shouldAllowPointerEventsOutside,
        shouldPreventBodyScroll,
        shouldResizeMenuToMatchTrigger,
        shouldShowMask,
        shouldShowSelectedCheck,
        shouldTruncateText,
        shouldEscapeViewport,
        triggerRef: forwardedRef,
        verticalItemAlignment,
      }),
      [
        activeIndex,
        ariaLabel,
        ariaLabelledBy,
        context,
        floatingStyles,
        forwardedRef,
        getFloatingProps,
        getItemProps,
        getReferenceProps,
        height,
        isNested,
        maskProps,
        minHeight,
        nodeId,
        parent,
        parentId,
        refs,
        shouldAllowPointerEventsOutside,
        shouldPreventBodyScroll,
        shouldResizeMenuToMatchTrigger,
        shouldShowMask,
        shouldShowSelectedCheck,
        shouldTruncateText,
        shouldEscapeViewport,
        verticalItemAlignment,
      ]
    );

    return (
      <FloatingNode id={nodeId}>
        <MenuContext.Provider value={menuContextValue}>
          {children}
        </MenuContext.Provider>
      </FloatingNode>
    );
  }
);

export const Menu = MenuFloatingWrapper;
